/**
 * Robust wrapper around React.lazy that handles chunk-load failures —
 * the #1 cause of blank pages after a redeploy on SPAs.
 *
 * Strategy:
 *  1. Retry the dynamic import N times with exponential backoff.
 *  2. If still failing, force a hard reload once per session (the user
 *     receives a fresh index.html with the new chunk hashes).
 *  3. After the one-time reload, give up and re-throw so the React
 *     error boundary can show a fallback.
 */
import { lazy } from 'react';

const RELOAD_FLAG = 'bonsai_chunk_reload';

function isChunkLoadError(err) {
  if (!err) return false;
  const msg = String(err.message || err);
  return (
    err.name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}

function retryImport(factory, attempts = 3, delay = 500) {
  return new Promise((resolve, reject) => {
    const tryOnce = (left) => {
      factory()
        .then(resolve)
        .catch((err) => {
          if (!isChunkLoadError(err) || left <= 1) {
            // If we've never reloaded this session, force a single hard reload
            // to fetch the latest index.html with up-to-date chunk hashes.
            try {
              if (typeof window !== 'undefined' && !sessionStorage.getItem(RELOAD_FLAG)) {
                sessionStorage.setItem(RELOAD_FLAG, '1');
                window.location.reload();
                return; // Promise stays pending; page is reloading.
              }
            } catch { /* sessionStorage may be unavailable */ }
            reject(err);
            return;
          }
          setTimeout(() => tryOnce(left - 1), delay);
        });
    };
    tryOnce(attempts);
  });
}

export function lazyWithRetry(factory) {
  return lazy(() => retryImport(factory));
}
